import { BrowserModule } from '@angular/platform-browser';
import { NgModule } from '@angular/core';

import { OverviewComponent } from './overview/components/overview/overview.component';
import { ActionReducer, MetaReducer, StoreModule } from '@ngrx/store';
import { reducers } from './store/reducers';
import { EffectsModule } from '@ngrx/effects';
import { ProductDetailsEffects } from './store/effects/product-details.effects';
import { ProductsEffects } from './store/effects/products.effects';
import { ProductsService } from './services/products.service';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { environment } from '../environments/environment';
import {
  AnalyticsModule,
  AnalyticsRouterModule,
  FormatterModule,
  TranslationModule,
} from '@proximus/ng-common';
import {
  AlertModule,
  BiptModule,
  CurrencyAmountModule,
  LoadingSpinnerModule,
  ModalMessageModule,
  ModalModule,
  PasswordModule,
} from '@proximus/ng-common-components';
import { ExceedingAmountComponent } from './overview/components/exceeding-amount/exceeding-amount.component';
import { ExceedingAmountService } from './services/exceeding-amount.service';
import { ExceedingAmountEffects } from './store/effects/exceeding-amount.effects';
import { FavAppComponent } from './overview/components/favapp/fav-app.component';
import { AddressSelectorComponent } from './overview/components/address-selector/address-selector.component';
import { JointOfferComponent } from './details/mobile/level-two/joint-offer/joint-offer.component';
import { PricingPlanComponent } from './details/mobile/level-two/pricing-plan/pricing-plan.component';
import { SimInfoService } from './services/sim-info.service';
import { SimInfoEffects } from './store/effects/sim-info.effects';
import { ChangeFavAppComponent } from './details/mobile/level-two/change-fav-app/change-fav-app.component';
import { MagnoliaConfigEffects } from './store/effects/magnolia-config.effects';
import { MagnoliaConfigService } from './services/magnolia-config.service';
import { MobileLevelTwoComponent } from './details/mobile/level-two/container/mobile-level-two.component';
import { MprdAppComponent } from './productapp/mprd-app.component';
import { PriceplanDetailsComponent } from './details/mobile/level-two/priceplan-details/priceplan-details.component';
import { OptionsCategoriesComponent } from './details/shared/options-categories/options-categories.component';
import { AvailableOptionsContainerComponent } from './details/mobile/level-two/available-options-container/available-options-container.component';
import { OptionComponent } from './details/shared/option/option.component';
import { OptionDetailsComponent } from './details/option-details/option-details.component';
import { DisneyPlusStandardPremiumComponent } from './details/option-details/option-details-background/options/disney-plus-standard-premium/disney-plus-standard-premium.component';
import { DisneyPlusOrderButtonComponent } from './details/option-details/option-details-background/disney-plus-order-button/disney-plus-order-button.component';
import { MobilePaymentComponent } from './details/option-details/mobile-payment/mobile-payment.component';
import { DeleteMandateComponent } from './details/option-details/delete-mandate/delete-mandate.component';
import { MpayService } from './services/mpay.service';
import { OptionLockService } from './services/option-lock.service';
import { AlarmThresholdComponent } from './details/option-details/alarm-threshold/alarm-threshold.component';
import { AlarmThresholdModalComponent } from './details/option-details/alarm-threshold/alarm-threshold-modal/alarm-threshold-modal.component';
import { AlarmThresholdEffects } from './store/effects/alarm-threshold-effects';
import { PrepaidSettingsService } from './services/prepaid-settings.service';
import { TvBonusComponent } from './details/tv/level-two/tv-bonus/tv-bonus.component';
import { TvLevelTwoComponent } from './details/tv/level-two/container/tv-level-two.component';
import { TvAvailableOptionsContainerComponent } from './details/tv/level-two/available-options-container/tv-available-options-container.component';
import { LanguageToggleComponent } from './details/tv/level-two/language-toggle/language-toggle.component';
import { OptionDetailsBackgroundComponent } from './details/option-details/option-details-background/option-details-background.component';
import { OptionDetailsBackgroundService } from './details/option-details/option-details-background/option-details-background.service';
import { OptionDetailsBaseComponent } from './details/option-details/option-details-base/option-details-base.component';
import { CardComponent } from './overview/components/card/card.component';
import { AvailableSpeedComponent } from './details/internet/level-two/available-speed/available-speed.component';
import { AvailableSpeedSkeletonComponent } from './details/internet/level-two/available-speed/available-speed-skeleton/available-speed-skeleton.component';
import { AvailableSpeedEffects } from './store/effects/available-speed.effects';
import { AvailableSpeedService } from './services/available-speed.service';
import { InternetSettingsComponent } from './details/internet/level-two/internet-settings/internet-settings.component';
import { InternetSettingsSkeletonComponent } from './details/internet/level-two/internet-settings/internet-settings-skeleton/internet-settings-skeleton.component';
import { UpdateInternetPasswordComponent } from './details/internet/level-two/internet-settings/update-internet-password/update-internet-password.component';
import { ModemSafetyComponent } from './details/internet/level-two/internet-settings/modem-safety/modem-safety.component';
import { SharedIpComponent } from './details/internet/level-two/internet-settings/shared-ip/shared-ip.component';
import { InternetSettingsEffects } from './store/effects/internet-settings.effects';
import { MyInternetAppsComponent } from './details/internet/level-two/my-internet-apps/my-internet-apps.component';
import { InternetLevelTwoComponent } from './details/internet/level-two/container/internet-level-two.component';
import { InternetAvailableOptionsContainerComponent } from './details/internet/level-two/internet-available-options-container/internet-available-options-container.component';
import { InternetAvailableOptionsSkeletonComponent } from './details/internet/level-two/internet-available-options-container/internet-available-options-skeleton/internet-available-options-skeleton.component';
import { SkeletonMobileLevelTwoComponent } from './details/mobile/level-two/skeleton/skeleton-mobile-level-two.component';
import { SkeletonTvLevelTwoComponent } from './details/tv/level-two/skeleton/skeleton-tv-level-two.component';
import { SkeletonInternetLevelTwoComponent } from './details/internet/level-two/skeleton/skeleton-internet-level-two.component';
import { SkeletonOverviewComponent } from './overview/components/skeleton/skeleton-overview.component';
import { SkeletonInternetOptionDetailsComponent } from './details/option-details/skeleton/internet/skeleton-internet-option-details.component';
import { SkeletonMobileOptionDetailsComponent } from './details/option-details/skeleton/mobile/skeleton-mobile-option-details.component';
import { SkeletonTvOptionDetailsComponent } from './details/option-details/skeleton/tv/skeleton-tv-option-details.component';
import { MailAppModule } from './mymail/mailapp.module';
import { MailboxesEffects } from './mymail/store/effects/mailboxes.effects';
import { ChangePasswordEffects } from './mymail/store/effects/change-password.effects';
import { PasswordRecoveryManagementEffects } from './mymail/store/effects/password-recovery-management.effects';
import { AliasEffects } from './mymail/store/effects/alias.effects';
import { NetflixAccountManagementComponent } from './details/option-details/netflix-account-management/netflix-account-management.component';
import { NetflixAccountManagementEffects } from './store/effects/netflix-account-management.effects';
import { NetflixAccountManagementService } from './services/netflix-account-management.service';
import { DisneyPlusAccountManagementService } from './services/disney-plus-account-management.service';
import { OrderConfirmationModalComponent } from './details/option-details/order-confirmation-modal/order-confirmation-modal.component';
import { OptionConfigurationLockService } from './services/option-configuration-lock.service';
import { AppRoutingModule } from './app-routing.module';
import { NortonModule } from './norton/norton.module';
import { PipeModule } from './pipe/pipe.module';
import { SharedModule } from './shared/shared.module';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CloudModule } from './cloud/cloud.module';
import { PublicWifiModule } from './public-wifi/public-wifi.module';
import { PublicWifiPxsplusModule } from './public-wifi-pxsplus/public-wifi-pxsplus.module';
import { UsageHeaderComponent } from './details/shared/usage-header/usage-header.component';
import { SimAndPukComponent } from './details/mobile/level-two/sim-and-puk/sim-and-puk.component';
import { ExcludeFavAppOptionsPipe } from './pipe/exclude-fav-app-options.pipe';
import { AvailableOptionsSkeletonComponent } from './details/mobile/level-two/available-options-container/available-options-skeleton/available-options-skeleton.component';
import { AvailableSettingsSkeletonComponent } from './details/mobile/level-two/available-settings-container/available-settings-skeleton/available-settings-skeleton.component';
import { AvailableSettingsContainerComponent } from './details/mobile/level-two/available-settings-container/available-settings-container.component';
import { JointOfferSkeletonComponent } from './details/mobile/level-two/joint-offer/joint-offer-skeleton/joint-offer-skeleton.component';
import { SettingComponent } from './details/shared/setting/setting.component';
import { ExcludeNetflixBuyAndSellPipe } from './pipe/exclude-netflix-buy-and-sell.pipe';
import { TvAvailableOptionsSkeletonComponent } from './details/tv/level-two/available-options-container/tv-available-options-skeleton/tv-available-options-skeleton.component';
import { NetflixBannerComponent } from './details/tv/level-two/netflix-banner/netflix-banner.component';
import { PublicWifiComponent } from './details/internet/level-two/public-wifi/public-wifi.component';
import { BizzGuestWifiComponent } from './details/option-details/bizz-guest-wifi/bizz-guest-wifi.component';
import { PriceplanDetailsSkeletonComponent } from './details/mobile/level-two/priceplan-details/priceplan-details-skeleton/priceplan-details-skeleton.component';
import { DataBoostComponent } from './details/mobile/level-two/data-boost/data-boost.component';
import { DataBoostSkeletonComponent } from './details/mobile/level-two/data-boost/data-boost-skeleton/data-boost-skeleton.component';
import { TelephonyLevelTwoComponent } from './details/telephony/level-two/container/telephony-level-two/telephony-level-two.component';
import { TelephonyLevelTwoSkeletonComponent } from './details/telephony/level-two/container/telephony-level-two/telephony-level-two-skeleton/telephony-level-two-skeleton.component';
import { DisneyPlusModalComponent } from './details/option-details/disney-plus-modal/disney-plus-modal.component';
import { EpressComponent } from './details/epress/epress.component';
import { EpressConfirmationModalComponent } from './details/epress/epress-confirmation-modal/epress-confirmation-modal.component';
import { EpressOptionComponent } from './details/epress/epress-option/epress-option.component';
import { EpressSkeletonComponent } from './details/epress/epress-skeleton/epress-skeleton.component';
import { DigitalisComponent } from './details/digitalis/components/container/digitalis/digitalis.component';
import { DigitalisSkeletonComponent } from './details/digitalis/components/container/digitalis/digitalis-skeleton/digitalis-skeleton.component';
import { ManageYourWebsitesComponent } from './details/digitalis/components/container/digitalis/manage-your-websites/manage-your-websites.component';
import { WebsiteOptionsComponent } from './details/digitalis/components/container/digitalis/website-options/website-options.component';
import { EmarketingComponent } from './details/digitalis/components/container/digitalis/emarketing/emarketing.component';
import { ManageYourAccountComponent } from './details/digitalis/components/container/digitalis/manage-your-account/manage-your-account.component';
import { WebsitesListComponent } from './details/digitalis/components/container/digitalis/manage-your-websites/websites-list/websites-list.component';
import { PayAndGoModalComponent } from './details/option-details/pay-and-go-modal/pay-and-go-modal.component';
import { PayAndGoEffects } from './store/effects/pay-and-go.effects';
import { HomeGatewayComponent } from './details/internet/level-two/internet-settings/home-gateway/home-gateway.component';
import { CreditAnnouncementsComponent } from './details/mobile/level-two/credit-announcements/credit-announcements.component';
import { BiptRedirectionComponent } from './bipt-redirection/bipt-redirection.component';
import { EasySwitchModule } from '@proximus/ng-common-components/src/lib/px-easy-switch';
import { HttpClientModule } from '@angular/common/http';
import { CyberCareModule } from './cyber-care/cyber-care.module';
import { CyberCareDetailsAllowedGuard } from './guards/cyber-care-details-allowed.guard';
import { InternetUsageDetailsComponent } from './details/internet/level-two/internet-usage-details/internet-usage-details.component';
import { PxCreditAnnouncementsModule, PxUsageModule } from '@dch/ng-usage';
import { WifiCredentialsComponent } from './details/internet/level-two/wifi-credentials/wifi-credentials.component';
import { WifiInformationEffects } from './store/effects/wifi-information.effects';
import { WifiCredentialsSkeletonComponent } from './details/internet/level-two/wifi-credentials/wifi-credentials-skeleton/wifi-credentials-skeleton.component';
import { BeatsSwapWarningModalComponent } from './details/mobile/level-two/priceplan-details/beats-swap-warning-modal/beats-swap-warning-modal.component';
import { ESimQrCodeComponent } from './details/mobile/level-two/e-sim-qr-code/e-sim-qr-code.component';
import { QRCodeModule } from 'angularx-qrcode';
import { SimContainerComponent } from './details/mobile/level-two/container/sim-container/sim-container.component';
import { InternetProductSelectorModalComponent } from './details/internet/level-two/internet-product-selector-modal/internet-product-selector-modal.component';
import { FiberMigrationComponent } from './overview/components/overview/fiber-migration/fiber-migration.component';
import { OffersEffects } from './store/effects/offers.effects';
import { OffersService } from './services/offers.service';
import { DisneyPlusAccountManagementEffects } from './store/effects/disney-plus-account-management.effects';
import { MyProximusAuthenticationCheckGuard } from './guards/myproximus-authentication-check.guard';
import { SmoothAccessTokenDisneyPlusGuard } from './smooth-access/guards/smooth-access-token-disney-plus.guard';
import { SmoothAccessService } from './services/smooth-access.service';
import { SmoothAccessEffects } from './store/effects/smooth-access.effects';
import { ExcludeFlexEmergencyPipe } from './pipe/exclude-flex-emergency.pipe';
import { NspModule } from './nsp/nsp.module';
import { NspDeeplinkGuard } from './guards/nsp-deeplink.guard';
import { localStorageSync } from 'ngrx-store-localstorage';
import { AccordionPanelComponent } from './overview/components/accordion-panel/accordion-panel.component';
import { InternetSettingsPxsplusComponent } from './details/internet/level-two/internet-settings/internet-settings-pxsplus/internet-settings-pxsplus.component';
import { SimAndPukPxsplusComponent } from './details/mobile/level-two/sim-and-puk-pxsplus/sim-and-puk-pxsplus.component';
import { MobileSettingsPxsplusComponent } from './details/mobile/level-two/mobile-settings-pxsplus/mobile-settings-pxsplus.component';
import { AddNewProductComponent } from './overview/components/overview/add-new-product/add-new-product.component';
import { MobileOptionsPxsplusComponent } from './details/mobile/level-two/mobile-options-pxsplus/mobile-options-pxsplus.component';
import { InternetUsagePxsplusComponent } from './details/internet/level-two/internet-usage-pxsplus/internet-usage-pxsplus.component';
import { InternetAvailableOptionsPxsplusComponent } from './details/internet/level-two/internet-available-options-pxsplus/internet-available-options-pxsplus.component';
import { TvUsagePxsplusComponent } from './details/tv/level-two/tv-usage-pxsplus/tv-usage-pxsplus.component';
import { MobileUsagePxsplusComponent } from './details/mobile/level-two/mobile-usage-pxsplus/mobile-usage-pxsplus.component';
import { EbuOptionDetailsComponent } from './details/option-details/ebu-option-details/ebu-option-details.component';
import { TvAvailableOptionsPxsplusComponent } from './details/tv/level-two/tv-available-options-pxsplus/tv-available-options-pxsplus.component';
import { SplitBillDetailComponent } from './details/mobile/level-two/split-bill-detail/split-bill-detail.component';
import { SplitUnbilledBalanceEffects } from './store/effects/split-unbilled-balance.effect';
import { SplitUnbilledBalanceService } from './services/split-unbilled-balance.service';
import { TelephonyAvailableOptionSkeletonComponent } from './details/telephony/level-two/telephony-available-option/telephony-available-option-skeleton/telephony-available-option-skeleton.component';
import { TelephonyAvailableOptionContainerComponent } from './details/telephony/level-two/telephony-available-option/telephony-available-option-container/telephony-available-option-container.component';
import { SkeletonTelephonyOptionDetailsComponent } from './details/option-details/skeleton/telephony/skeleton-telephony-option-details.component';
import { ApolloRunnerService } from './services/apollo-runner.service';
import { ApolloModule } from 'apollo-angular';
import { LoggerService } from './services/logger.service';
import { EbuOrderingEffects } from './store/effects/ebu-ordering-effects';
import { EbuOrderConfirmationModalComponent } from './details/option-details/ebu-order-confirmation-modal/ebu-order-confirmation-modal.component';
import { EbuOrderValidationModalComponent } from './details/option-details/ebu-order-validation-modal/ebu-order-validation-modal.component';
import { FlexEmergencyManagePxsplusComponent } from './details/internet/level-two/flex-emergency/flex-emergency-manage-pxsplus/flex-emergency-manage-pxsplus.component';
import { FlexProductCardComponent } from './details/internet/level-two/flex-emergency/flex-product-card/flex-product-card.component';
import { FlexProductCardSkeletonComponent } from './details/internet/level-two/flex-emergency/flex-product-card-skeleton/flex-product-card-skeleton.component';
import { FlexEmergencyEntryPxsplusComponent } from './details/internet/level-two/flex-emergency/flex-emergency-entry-pxsplus/flex-emergency-entry-pxsplus.component';
import { FlexEmergencyActivationPxsplusComponent } from './details/internet/level-two/flex-emergency/flex-emergency-activation-pxsplus/flex-emergency-activation-pxsplus.component';
import { FlexEmergencyConfirmationPxsplusComponent } from './details/internet/level-two/flex-emergency/flex-emergency-confirmation-pxsplus/flex-emergency-confirmation-pxsplus.component';
import { FlexEmergencyManageSkeletonPxsplusComponent } from './details/internet/level-two/flex-emergency/flex-emergency-manage-skeleton-pxsplus/flex-emergency-manage-skeleton-pxsplus.component';
import { FixedIpComponent } from './details/internet/level-two/internet-settings/fixed-ip/fixed-ip.component';
import { MobileSelectorComponent } from './overview/components/overview/add-new-product/mobile-selector/mobile-selector.component';
import { OrderFiberBoostConfirmationModalComponent } from './details/option-details/order-fiber-boost-confirmation-modal/order-fiber-boost-confirmation-modal.component';
import { SimComponent } from './sim/sim.component';
import { SimReplaceComponent } from './sim/sim-replace/sim-replace.component';
import { SimTransferComponent } from './sim/sim-transfer/sim-transfer.component';
import { SimPhysicalComponent } from './sim/sim-replace/sim-physical/sim-physical.component';
import {
  ActionMenuModule,
  OverlayerModule,
} from '@proximus/pxs-angular-design-system';
import { ModemChoiceComponent } from './modem-choice/modem-choice.component';
import { ModemChoiceEntrypointComponent } from './details/internet/level-two/internet-settings/modem-choice-entrypoint/modem-choice-entrypoint.component';
import { ModemChoiceOntComponent } from './modem-choice/modem-choice-onts/modem-choice-ont/modem-choice-ont.component';
import { ModemChoiceAddOntComponent } from './modem-choice/modem-choice-add-ont/modem-choice-add-ont.component';
import { ModemChoiceDeleteOntComponent } from './modem-choice/modem-choice-delete-ont/modem-choice-delete-ont.component';
import { ModemChoiceEditOntComponent } from './modem-choice/modem-choice-edit-ont/modem-choice-edit-ont.component';
import { ModemChoiceVoipCredentialsComponent } from './modem-choice/modem-choice-voip-credentials/modem-choice-voip-credentials.component';
import { ModemChoiceVlanComponent } from './modem-choice/modem-choice-vlan/modem-choice-vlan.component';
import { ModemChoiceVoipComponent } from './modem-choice/modem-choice-voip/modem-choice-voip.component';
import { ModemChoiceOntsComponent } from './modem-choice/modem-choice-onts/modem-choice-onts.component';
import { ModemChoiceOntsSkeletonComponent } from './modem-choice/modem-choice-onts/modem-choice-onts-skeleton/modem-choice-onts-skeleton.component';
import { ModemChoiceVlanSkeletonComponent } from './modem-choice/modem-choice-vlan/modem-choice-vlan-skeleton/modem-choice-vlan-skeleton.component';
import { ModemChoiceVoipSkeletonComponent } from './modem-choice/modem-choice-voip/modem-choice-voip-skeleton/modem-choice-voip-skeleton.component';
import { UserAccessManagementComponent } from './details/user-access/user-access-management/user-access-management.component';
import { UserAccessService } from './details/user-access/user-access.service';
import { UserAccessManagementSkeletonComponent } from './details/user-access/user-access-management-skeleton/user-access-management-skeleton.component';
import { AlwaysProtectedCheckService } from './services/always-protected-check.service';
import { AlwaysProtectedComponent } from './always-protected/always-protected.component';
import { NortonProductCheckService } from './services/norton-product-check.service';
import { GetJwtRedirectComponent } from './get-jwt-redirect/get-jwt-redirect.component';
import { SportsStandardPremiumComponent } from './details/option-details/option-details-background/options/sports-standard-premium/sports-standard-premium.component';
import { SportsOrderButtonComponent } from './details/option-details/option-details-background/options/sports-standard-premium/sports-order-button.component';

// Used for NSP activation flow. If the user refreshes the page new on step 2, we can then recover what he entered on step 1.
export function localStorageSyncReducer(
  reducer: ActionReducer<any>,
): ActionReducer<any> {
  return localStorageSync({ keys: ['nsp'], rehydrate: true })(reducer);
}
const metaReducers: Array<MetaReducer<any, any>> = [localStorageSyncReducer];

declare global {
  interface Window {
    ReactNativeWebView?: any;
  }
}

@NgModule({
  declarations: [
    MprdAppComponent,
    OverviewComponent,
    AddressSelectorComponent,
    ExceedingAmountComponent,
    FavAppComponent,
    JointOfferComponent,
    PricingPlanComponent,
    ChangeFavAppComponent,
    MobileLevelTwoComponent,
    PriceplanDetailsComponent,
    AvailableOptionsContainerComponent,
    OptionsCategoriesComponent,
    OptionComponent,
    OptionDetailsComponent,
    MobilePaymentComponent,
    DeleteMandateComponent,
    AlarmThresholdComponent,
    AlarmThresholdModalComponent,
    TvBonusComponent,
    TvLevelTwoComponent,
    TvAvailableOptionsContainerComponent,
    LanguageToggleComponent,
    OptionDetailsBackgroundComponent,
    DisneyPlusStandardPremiumComponent,
    SportsStandardPremiumComponent,
    SportsOrderButtonComponent,
    DisneyPlusOrderButtonComponent,
    OptionDetailsBaseComponent,
    CardComponent,
    InternetSettingsComponent,
    InternetSettingsSkeletonComponent,
    UpdateInternetPasswordComponent,
    ModemSafetyComponent,
    SharedIpComponent,
    CardComponent,
    OptionDetailsBaseComponent,
    AvailableSpeedComponent,
    AvailableSpeedSkeletonComponent,
    MyInternetAppsComponent,
    PublicWifiComponent,
    InternetLevelTwoComponent,
    UsageHeaderComponent,
    InternetAvailableOptionsContainerComponent,
    InternetAvailableOptionsSkeletonComponent,
    SkeletonMobileLevelTwoComponent,
    SkeletonTvLevelTwoComponent,
    SkeletonInternetLevelTwoComponent,
    SkeletonOverviewComponent,
    SkeletonInternetOptionDetailsComponent,
    SkeletonMobileOptionDetailsComponent,
    SkeletonTvOptionDetailsComponent,
    NetflixAccountManagementComponent,
    OrderConfirmationModalComponent,
    SimAndPukComponent,
    AvailableOptionsSkeletonComponent,
    AvailableSettingsSkeletonComponent,
    AvailableSettingsContainerComponent,
    JointOfferSkeletonComponent,
    SettingComponent,
    ExcludeNetflixBuyAndSellPipe,
    TvAvailableOptionsSkeletonComponent,
    NetflixBannerComponent,
    PriceplanDetailsSkeletonComponent,
    BizzGuestWifiComponent,
    DataBoostComponent,
    DataBoostSkeletonComponent,
    TelephonyLevelTwoComponent,
    TelephonyLevelTwoSkeletonComponent,
    DisneyPlusModalComponent,
    EpressComponent,
    EpressConfirmationModalComponent,
    EpressOptionComponent,
    EpressSkeletonComponent,
    DigitalisComponent,
    DigitalisSkeletonComponent,
    ManageYourWebsitesComponent,
    WebsiteOptionsComponent,
    EmarketingComponent,
    ManageYourAccountComponent,
    WebsitesListComponent,
    PayAndGoModalComponent,
    HomeGatewayComponent,
    CreditAnnouncementsComponent,
    BiptRedirectionComponent,
    ESimQrCodeComponent,
    InternetUsageDetailsComponent,
    BeatsSwapWarningModalComponent,
    WifiCredentialsComponent,
    WifiCredentialsSkeletonComponent,
    SimContainerComponent,
    InternetProductSelectorModalComponent,
    FiberMigrationComponent,
    AccordionPanelComponent,
    InternetSettingsPxsplusComponent,
    SimAndPukPxsplusComponent,
    MobileSettingsPxsplusComponent,
    AddNewProductComponent,
    MobileOptionsPxsplusComponent,
    InternetUsagePxsplusComponent,
    InternetAvailableOptionsPxsplusComponent,
    TvUsagePxsplusComponent,
    MobileUsagePxsplusComponent,
    EbuOptionDetailsComponent,
    TvAvailableOptionsPxsplusComponent,
    SplitBillDetailComponent,
    TelephonyAvailableOptionSkeletonComponent,
    TelephonyAvailableOptionContainerComponent,
    SkeletonTelephonyOptionDetailsComponent,
    EbuOrderConfirmationModalComponent,
    EbuOrderValidationModalComponent,
    FlexEmergencyManagePxsplusComponent,
    FlexEmergencyManageSkeletonPxsplusComponent,
    FlexProductCardComponent,
    FlexProductCardSkeletonComponent,
    FlexEmergencyEntryPxsplusComponent,
    FlexEmergencyActivationPxsplusComponent,
    FlexEmergencyConfirmationPxsplusComponent,
    FixedIpComponent,
    MobileSelectorComponent,
    OrderFiberBoostConfirmationModalComponent,
    SimComponent,
    SimPhysicalComponent,
    SimReplaceComponent,
    SimTransferComponent,
    ModemChoiceAddOntComponent,
    ModemChoiceComponent,
    ModemChoiceDeleteOntComponent,
    ModemChoiceEditOntComponent,
    ModemChoiceEntrypointComponent,
    ModemChoiceOntComponent,
    ModemChoiceOntsComponent,
    ModemChoiceOntsSkeletonComponent,
    ModemChoiceVlanComponent,
    ModemChoiceVlanSkeletonComponent,
    ModemChoiceVoipComponent,
    ModemChoiceVoipCredentialsComponent,
    ModemChoiceVoipSkeletonComponent,
    UserAccessManagementComponent,
    UserAccessManagementSkeletonComponent,
    AlwaysProtectedComponent,
    GetJwtRedirectComponent,
  ],
  imports: [
    BrowserModule,
    HttpClientModule,
    TranslationModule,
    PasswordModule,
    StoreModule.forRoot(reducers, {
      metaReducers,
      runtimeChecks: {
        strictStateImmutability: false,
        strictActionImmutability: false,
      },
    }),
    StoreDevtoolsModule.instrument({
      maxAge: 25,
      logOnly: environment.production,
    }),
    EffectsModule.forRoot([
      ProductDetailsEffects,
      ProductsEffects,
      ExceedingAmountEffects,
      SimInfoEffects,
      MagnoliaConfigEffects,
      AlarmThresholdEffects,
      AvailableSpeedEffects,
      InternetSettingsEffects,
      MailboxesEffects,
      ChangePasswordEffects,
      PasswordRecoveryManagementEffects,
      AliasEffects,
      NetflixAccountManagementEffects,
      DisneyPlusAccountManagementEffects,
      PayAndGoEffects,
      WifiInformationEffects,
      OffersEffects,
      SmoothAccessEffects,
      SplitUnbilledBalanceEffects,
      EbuOrderingEffects,
    ]),
    ModalModule,
    ModalMessageModule,
    AlertModule,
    FormatterModule,
    CurrencyAmountModule,
    FormsModule,
    ReactiveFormsModule,
    PxUsageModule,
    BiptModule,
    MailAppModule,
    AnalyticsModule,
    AnalyticsRouterModule,
    NortonModule,
    PipeModule,
    SharedModule,
    CloudModule,
    PublicWifiModule,
    PublicWifiPxsplusModule,
    PxCreditAnnouncementsModule,
    EasySwitchModule,
    CyberCareModule,
    LoadingSpinnerModule,
    QRCodeModule,
    NspModule,
    ApolloModule,
    OverlayerModule,
    ActionMenuModule,
    AppRoutingModule, // MUST be the last module to manage default route correctly
  ],
  providers: [
    ProductsService,
    ExceedingAmountService,
    SimInfoService,
    MagnoliaConfigService,
    MpayService,
    OptionConfigurationLockService,
    OptionLockService,
    PrepaidSettingsService,
    AvailableSpeedService,
    NetflixAccountManagementService,
    DisneyPlusAccountManagementService,
    ExcludeFavAppOptionsPipe,
    ExcludeNetflixBuyAndSellPipe,
    CyberCareDetailsAllowedGuard,
    OffersService,
    MyProximusAuthenticationCheckGuard,
    SmoothAccessTokenDisneyPlusGuard,
    SmoothAccessService,
    ExcludeFlexEmergencyPipe,
    NspDeeplinkGuard,
    SplitUnbilledBalanceService,
    LoggerService,
    ApolloRunnerService,
    UserAccessService,
    OptionDetailsBackgroundService,
    { provide: 'graphqlUrl', useValue: window.location.host + '/graphql/ebu/' },
    AlwaysProtectedCheckService,
    NortonProductCheckService,
  ],
  bootstrap: [MprdAppComponent],
})
export class AppModule {}
